import { Reveal, SectionHeading } from "@/components/motion";

const SIGNATURES = [
  { tag: "impossible_travel_speed", sev: 0.95, what: "Reported speed exceeds anything physically plausible — the device is lying about where it is, or how it got there." },
  { tag: "location_jump", sev: 0.9, what: "Two consecutive fixes imply teleportation — movement no vehicle on earth can make." },
  { tag: "claim_contradiction", sev: 0.8, what: "The driver claims \u2018delivered\u2019. Every other signal says otherwise." },
  { tag: "no_go_zone_entered", sev: 0.7, what: "The event was recorded inside a zone your own ruleset forbids." },
  { tag: "gps_drift_suspected", sev: 0.6, what: "Positional accuracy collapses at exactly the moment the claim is made." },
  { tag: "bearing_mismatch", sev: 0.55, what: "Device heading disagrees with the actual movement vector — replayed tracks carry stale headings." },
  { tag: "speed_trace_mismatch", sev: 0.5, what: "Reported speed doesn\u2019t match the distance covered between fixes — fabricated telemetry rarely aligns both." },
  { tag: "evidence_time_gap", sev: 0.45, what: "The proof photo was taken too far from the claimed event time." },
  { tag: "arrived_at_speed", sev: 0.4, what: "A \u2018delivery\u2019 claimed while the device was still moving. Real stops decelerate first." },
  { tag: "missing_evidence", sev: 0.4, what: "Required photo proof is absent — or stripped of GPS and timestamp metadata." },
];

const AnomalyShowcase = () => (
  <section data-testid="anomaly-showcase-section" className="border-b border-border bg-muted/30">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32">
      <div className="grid lg:grid-cols-12 gap-12 mb-16">
        <div className="lg:col-span-7">
          <SectionHeading index="03" eyebrow="The Anomaly Engine" title="What we catch that humans miss." />
        </div>
        <div className="lg:col-span-5 lg:pt-16 flex items-end">
          <Reveal delay={0.15}>
            <p className="text-base leading-relaxed text-muted-foreground max-w-md">
              Ten spoofing and fraud signatures evaluated on every single event — each with a
              severity score and a recommended action, before the payout goes through.
            </p>
          </Reveal>
        </div>
      </div>
      <Reveal delay={0.1}>
        <div className="border border-border divide-y divide-border">
          <div className="hidden md:grid grid-cols-12 px-6 py-3 font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground">
            <span className="col-span-4">Signature</span>
            <span className="col-span-6">What it catches</span>
            <span className="col-span-2 text-right">Severity</span>
          </div>
          {SIGNATURES.map((s) => (
            <div
              key={s.tag}
              data-testid={`anomaly-row-${s.tag}`}
              className="grid md:grid-cols-12 gap-2 md:gap-0 px-6 py-5 items-center hover:bg-muted/50 transition-colors duration-200 group"
            >
              <span className="md:col-span-4 font-mono text-xs text-anomaly tracking-wide">
                {s.tag}
              </span>
              <span className="md:col-span-6 text-sm text-muted-foreground group-hover:text-foreground transition-colors duration-200 pr-6">
                {s.what}
              </span>
              <span className="md:col-span-2 flex md:justify-end items-center gap-3">
                <span className="h-1 w-20 bg-border overflow-hidden hidden sm:block">
                  <span
                    className="block h-full bg-anomaly"
                    style={{ width: `${s.sev * 100}%` }}
                  />
                </span>
                <span className="font-mono text-xs text-foreground">{s.sev.toFixed(2)}</span>
              </span>
            </div>
          ))}
        </div>
      </Reveal>
      <Reveal delay={0.2}>
        <p className="mt-8 font-mono text-[11px] tracking-[0.2em] uppercase text-muted-foreground">
          Same signatures, live — <span className="text-telemetry">run them in the playground below</span>
        </p>
      </Reveal>
    </div>
  </section>
);

export default AnomalyShowcase;
